(() => {
  console.log('Pink Fink frame preview loaded');

  const previewStates = [];

  const getProductOptionSelects = (root = document) => {
    const searchArea =
      root &&
      typeof root.querySelectorAll === 'function'
        ? root
        : document;

    let selects = [
      ...searchArea.querySelectorAll(
        'select[id^="SingleOptionSelector"]'
      )
    ];

    /*
     * Fall back to the complete document only when no
     * product selectors were found inside the product root.
     */
    if (
      selects.length === 0 &&
      searchArea !== document
    ) {
      selects = [
        ...document.querySelectorAll(
          'select[id^="SingleOptionSelector"]'
        )
      ];
    }

    return selects;
  };

  const normaliseOptionValue = (value) => {
    return String(value || '')
      .trim()
      .toLowerCase();
  };

  /*
   * Reads a product tag such as:
   *
   * frame-ratio-80x90
   * frame-ratio-61x91
   * frame-ratio-100x100
   *
   * Returns width divided by height.
   */
  const getTaggedArtworkRatio = (
    productTags
  ) => {
    const ratioTag = productTags.find(
      (tag) =>
        /^frame-ratio-\d+(?:\.\d+)?x\d+(?:\.\d+)?$/.test(
          tag
        )
    );

    if (!ratioTag) {
      return null;
    }

    const dimensions = ratioTag.match(
      /^frame-ratio-(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/
    );

    if (!dimensions) {
      return null;
    }

    const width = Number(dimensions[1]);
    const height = Number(dimensions[2]);

    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      return null;
    }

    return width / height;
  };

  const isFrameSelect = (select) => {
    if (
      !(
        select instanceof
        HTMLSelectElement
      )
    ) {
      return false;
    }

    const optionValues = [
      ...select.options
    ].map((option) => {
      const value =
        normaliseOptionValue(option.value);

      const text =
        normaliseOptionValue(
          option.textContent
        );

      return value || text;
    });

    const requiredFrameValues = [
      'unframed',
      'natural',
      'black',
      'white'
    ];

    return requiredFrameValues.every(
      (requiredValue) =>
        optionValues.some(
          (optionValue) =>
            optionValue === requiredValue
        )
    );
  };

  const isSizeSelect = (select) => {
    if (
      !(
        select instanceof
        HTMLSelectElement
      )
    ) {
      return false;
    }

    if (isFrameSelect(select)) {
      return false;
    }

    return [...select.options].some(
      (option) => {
        const optionValue =
          option.value ||
          option.textContent ||
          '';

        return /\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?/i.test(
          optionValue
        );
      }
    );
  };

  const getFrameSelect = (
    root = document
  ) => {
    const selects =
      getProductOptionSelects(root);

    return (
      selects.find((select) =>
        isFrameSelect(select)
      ) || null
    );
  };

  const getSizeSelect = (
    root = document
  ) => {
    const selects =
      getProductOptionSelects(root);

    return (
      selects.find((select) =>
        isSizeSelect(select)
      ) || null
    );
  };

  const getSelectedSizeValue = (
    root = document
  ) => {
    const sizeSelect =
      getSizeSelect(root);

    return sizeSelect?.value || '';
  };

  const getSelectedFrameValue = (
    root = document
  ) => {
    const frameSelect =
      getFrameSelect(root);

    return frameSelect?.value ||
      'Unframed';
  };

  const syncPinkFinkThumbnailHeight = (
    slide
  ) => {
    const photos =
      slide.closest('.product__photos');

    const mainPhotos =
      photos?.querySelector(
        '.product__main-photos'
      );

    const thumbnails =
      photos?.querySelector(
        '.product__thumbs:not(.product__thumbs--below)'
      );

    const scroller =
      thumbnails?.querySelector(
        '.product__thumbs--scroller'
      );

    if (
      !photos ||
      !mainPhotos ||
      !thumbnails ||
      !scroller
    ) {
      return;
    }

    if (window.innerWidth <= 798) {
      thumbnails.style.removeProperty(
        'height'
      );

      thumbnails.style.removeProperty(
        'max-height'
      );

      scroller.style.removeProperty(
        'height'
      );

      scroller.style.removeProperty(
        'max-height'
      );

      scroller.style.removeProperty(
        'overflow-y'
      );

      scroller.style.removeProperty(
        'overflow-x'
      );

      return;
    }

    const galleryHeight = Math.round(
      mainPhotos.getBoundingClientRect()
        .height
    );

    if (galleryHeight <= 0) {
      return;
    }

    thumbnails.style.height =
      `${galleryHeight}px`;

    thumbnails.style.maxHeight =
      `${galleryHeight}px`;

    scroller.style.height =
      `${galleryHeight}px`;

    scroller.style.maxHeight =
      `${galleryHeight}px`;

    scroller.style.overflowY = 'auto';
    scroller.style.overflowX = 'hidden';
  };

  const switchGalleryToFirstImage = (
    slide
  ) => {
    const photos =
      slide.closest('.product__photos');

    if (!photos) {
      return;
    }

    const firstThumbnail =
      photos.querySelector(
        [
          '.product__thumb-item a[data-index="0"]',
          '.product__thumb[data-index="0"]',
          '[data-product-thumb][data-index="0"]',
          '.product__thumb-item:first-child a',
          '.product__thumbs--scroller a:first-child'
        ].join(', ')
      );

    if (
      firstThumbnail instanceof
      HTMLElement
    ) {
      firstThumbnail.click();
    }
  };

  const setStageOrientation = (
    state,
    orientation,
    displayedWidth,
    displayedHeight
  ) => {
    state.stage.classList.remove(
      'is-portrait',
      'is-landscape',
      'is-square'
    );

    state.stage.classList.add(
      `is-${orientation}`
    );

    state.stage.style.aspectRatio =
      `${displayedWidth} / ${displayedHeight}`;
  };

  const applySelectedAspectRatio = (
    state
  ) => {
    const selectedSize =
      getSelectedSizeValue(state.root);

    /*
     * Canvas prints:
     * use the selected size option.
     */
    if (selectedSize) {
      const normalisedSize =
        String(selectedSize)
          .toLowerCase()
          .replace(/×/g, 'x')
          .replace(/\s+/g, '');

      const dimensions =
        normalisedSize.match(
          /(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)/
        );

      if (dimensions) {
        const firstDimension =
          Number(dimensions[1]);

        const secondDimension =
          Number(dimensions[2]);

        if (
          Number.isFinite(
            firstDimension
          ) &&
          Number.isFinite(
            secondDimension
          ) &&
          firstDimension > 0 &&
          secondDimension > 0
        ) {
          const isSquare =
            Math.abs(
              firstDimension -
              secondDimension
            ) < 0.01;

          const smallerDimension =
            Math.min(
              firstDimension,
              secondDimension
            );

          const largerDimension =
            Math.max(
              firstDimension,
              secondDimension
            );

          if (isSquare) {
            setStageOrientation(
              state,
              'square',
              1,
              1
            );

            return;
          }

          if (
            state.artworkOrientation ===
            'landscape'
          ) {
            setStageOrientation(
              state,
              'landscape',
              largerDimension,
              smallerDimension
            );

            return;
          }

          setStageOrientation(
            state,
            'portrait',
            smallerDimension,
            largerDimension
          );

          return;
        }
      }

      console.warn(
        'Pink Fink could not read the selected size:',
        selectedSize
      );
    }

    /*
     * Oil paintings:
     * no size dropdown exists.
     */
    if (
      state.artworkOrientation ===
      'square'
    ) {
      /*
       * Keep using the square frame PNG, but let the
       * stage use the painting's actual proportions.
       *
       * Example:
       * frame-ratio-80x90 gives 80 / 90.
       */
      const artworkRatio =
        Number.isFinite(
          state.artworkAspectRatio
        ) &&
        state.artworkAspectRatio > 0
          ? state.artworkAspectRatio
          : 1;

      setStageOrientation(
        state,
        'square',
        artworkRatio,
        1
      );

      return;
    }

    if (
      state.artworkOrientation ===
      'landscape'
    ) {
      setStageOrientation(
        state,
        'landscape',
        state.artworkAspectRatio > 1
          ? state.artworkAspectRatio
          : 3,
        1
      );

      return;
    }

    setStageOrientation(
      state,
      'portrait',
      state.artworkAspectRatio > 0 &&
      state.artworkAspectRatio < 1
        ? state.artworkAspectRatio
        : 2 / 3,
      1
    );
  };

  const selectFrameUrl = (
    state,
    frameValue
  ) => {
    const isLandscape =
      state.stage.classList.contains(
        'is-landscape'
      );

    const isSquare =
      state.stage.classList.contains(
        'is-square'
      );

    const isBlack =
      frameValue.includes('black');

    const isWhite =
      frameValue.includes('white');

    if (isLandscape) {
      if (isBlack) {
        return (
          state.blackLandscapeFrameUrl
        );
      }

      if (isWhite) {
        return (
          state.whiteLandscapeFrameUrl
        );
      }

      return (
        state.naturalLandscapeFrameUrl
      );
    }

    if (isSquare) {
      if (isBlack) {
        return state.blackSquareFrameUrl;
      }

      if (isWhite) {
        return state.whiteSquareFrameUrl;
      }

      return state.naturalSquareFrameUrl;
    }

    if (isBlack) {
      return state.blackFrameUrl;
    }

    if (isWhite) {
      return state.whiteFrameUrl;
    }

    return state.naturalFrameUrl;
  };

  const applyFrameSelection = (
    state,
    selectedValue
  ) => {
    const {
      stage,
      frame,
      artworkLayer,
      innerShadow
    } = state;

    const frameValue =
      normaliseOptionValue(
        selectedValue
      );

    const isUnframed =
      frameValue === '' ||
      frameValue.includes('unframed') ||
      frameValue.includes('no frame');

    /*
     * For square-frame override products, the stage
     * already follows the painting's real proportions.
     * Stretch the artwork layer to that same opening.
     */
    const artworkBackgroundSize = 'cover';

    if (isUnframed) {
      frame.style.setProperty(
        'display',
        'none',
        'important'
      );

      stage.style.setProperty(
        '--frame-inset',
        '0%'
      );

      artworkLayer.style.setProperty(
        'background-size',
        artworkBackgroundSize,
        'important'
      );

      artworkLayer.style.setProperty(
        'background-color',
        'transparent',
        'important'
      );

      artworkLayer.style.setProperty(
        'background-position',
        'center center',
        'important'
      );

      artworkLayer.style.setProperty(
        'box-shadow',
        'none',
        'important'
      );

      innerShadow.style.setProperty(
        'display',
        'none',
        'important'
      );

      return;
    }

    const selectedFrameUrl =
      selectFrameUrl(
        state,
        frameValue
      );

    if (!selectedFrameUrl) {
      console.warn(
        'Pink Fink frame URL was not found.',
        {
          frameValue,
          stageClasses:
            state.stage.className
        }
      );

      return;
    }

    frame.src = selectedFrameUrl;

    frame.style.removeProperty(
      'display'
    );

    stage.style.removeProperty(
      '--frame-inset'
    );

    artworkLayer.style.setProperty(
      'background-size',
      artworkBackgroundSize,
      'important'
    );

    artworkLayer.style.setProperty(
      'background-color',
      'transparent',
      'important'
    );

    artworkLayer.style.setProperty(
      'background-position',
      'center center',
      'important'
    );

    artworkLayer.style.setProperty(
      'box-shadow',
      'none',
      'important'
    );

    innerShadow.style.setProperty(
      'display',
      'none',
      'important'
    );
  };

  const activatePreview = (state) => {
    const selectedFrameValue =
      getSelectedFrameValue(state.root);

    /*
     * Set the stage ratio first because the selected
     * frame file depends on the orientation class.
     */
    applySelectedAspectRatio(state);

    applyFrameSelection(
      state,
      selectedFrameValue
    );

    state.composite.style.removeProperty(
      'display'
    );

    state.slide.classList.add(
      'pink-fink-ready'
    );

    window.requestAnimationFrame(() => {
      switchGalleryToFirstImage(
        state.slide
      );

      syncPinkFinkThumbnailHeight(
        state.slide
      );
    });
  };

  const initialisePinkFinkPreview = () => {
    const slides =
      document.querySelectorAll(
        '[data-pink-fink-frame-preview]:not([data-pink-fink-initialised])'
      );

    slides.forEach((slide) => {
      slide.setAttribute(
        'data-pink-fink-initialised',
        'true'
      );

      const artworkUrl =
        slide.dataset.pinkFinkArtwork;

      const artworkAlt =
        slide.dataset
          .pinkFinkArtworkAlt || '';

      /*
       * Portrait frame files.
       */
      const naturalFrameUrl =
        slide.dataset
          .pinkFinkNaturalFrame;

      const blackFrameUrl =
        slide.dataset
          .pinkFinkBlackFrame;

      const whiteFrameUrl =
        slide.dataset
          .pinkFinkWhiteFrame;

      /*
       * Landscape frame files.
       */
      const naturalLandscapeFrameUrl =
        slide.dataset
          .pinkFinkNaturalLandscapeFrame;

      const blackLandscapeFrameUrl =
        slide.dataset
          .pinkFinkBlackLandscapeFrame;

      const whiteLandscapeFrameUrl =
        slide.dataset
          .pinkFinkWhiteLandscapeFrame;

      /*
       * Square frame files.
       */
      const naturalSquareFrameUrl =
        slide.dataset
          .pinkFinkNaturalSquareFrame;

      const blackSquareFrameUrl =
        slide.dataset
          .pinkFinkBlackSquareFrame;

      const whiteSquareFrameUrl =
        slide.dataset
          .pinkFinkWhiteSquareFrame;

      const imageMain =
        slide.querySelector(
          '[data-product-image-main]'
        );

      const root =
        slide.closest(
          '.product-section'
        ) ||
        slide.closest(
          '[data-section-type="product"]'
        ) ||
        slide.closest(
          '.product-template'
        ) ||
        slide.closest(
          '[data-section-id]'
        ) ||
        document;

      if (!artworkUrl || !imageMain) {
        console.warn(
          'Pink Fink preview could not initialise.',
          {
            artworkUrl,
            imageMain
          }
        );

        return;
      }

      const composite =
        document.createElement('div');

      composite.className =
        'pink-fink-composite';

      /*
       * Keep the regular gallery image visible until the
       * customer changes a product option.
       */
      composite.style.display = 'none';

      const stage =
        document.createElement('div');

      stage.className =
        'pink-fink-composite__stage';

      const artwork = new Image();

      artwork.src = artworkUrl;
      artwork.alt = artworkAlt;
      artwork.loading = 'eager';

      const artworkLayer =
        document.createElement('div');

      artworkLayer.className =
        'pink-fink-composite__artwork-layer';

      artworkLayer.style.backgroundImage =
        `url("${artworkUrl}")`;

      artworkLayer.style.backgroundSize =
        'cover';

      artworkLayer.style.backgroundPosition =
        'center center';

      artworkLayer.style.backgroundRepeat =
        'no-repeat';

      artworkLayer.style.backgroundColor =
        'transparent';

      artworkLayer.style.boxShadow =
        'none';

      const innerShadow =
        document.createElement('span');

      innerShadow.className =
        'pink-fink-composite__inner-shadow';

      innerShadow.setAttribute(
        'aria-hidden',
        'true'
      );

      innerShadow.style.setProperty(
        'display',
        'none',
        'important'
      );

      const frame =
        document.createElement('img');

      frame.className =
        'pink-fink-composite__frame';

      frame.alt = '';

      frame.setAttribute(
        'aria-hidden',
        'true'
      );

      frame.style.setProperty(
        'display',
        'none',
        'important'
      );

      let previewFinished = false;

      const finishPreview = () => {
        if (previewFinished) {
          return;
        }

        previewFinished = true;

        const naturalWidth =
          artwork.naturalWidth;

        const naturalHeight =
          artwork.naturalHeight;

        const detectedArtworkAspectRatio =
          naturalWidth / naturalHeight;

        const productTags = String(
          slide.dataset
            .pinkFinkProductTags || ''
        )
          .toLowerCase()
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);

        const taggedArtworkRatio =
          getTaggedArtworkRatio(
            productTags
          );

        /*
         * Use the frame-ratio tag when supplied.
         * Otherwise use the gallery image ratio.
         */
        const artworkAspectRatio =
          taggedArtworkRatio ||
          detectedArtworkAspectRatio;

        const forceSquare =
          productTags.includes(
            'force-square-frame'
          );

        const forcePortrait =
          productTags.includes(
            'force-portrait-frame'
          );

        const forceLandscape =
          productTags.includes(
            'force-landscape-frame'
          );

        const isSquareArtwork =
          Math.abs(
            artworkAspectRatio - 1
          ) <= 0.05;

        let artworkOrientation;

        if (forceSquare) {
          artworkOrientation = 'square';
        } else if (forcePortrait) {
          artworkOrientation = 'portrait';
        } else if (forceLandscape) {
          artworkOrientation = 'landscape';
        } else if (isSquareArtwork) {
          artworkOrientation = 'square';
        } else if (
          artworkAspectRatio > 1
        ) {
          artworkOrientation = 'landscape';
        } else {
          artworkOrientation = 'portrait';
        }

        /*
         * Set the initial stage ratio.
         *
         * force-square-frame chooses the square PNG.
         * frame-ratio-80x90 supplies the real shape.
         */
        if (
          artworkOrientation === 'square'
        ) {
          stage.classList.add(
            'is-square'
          );

          stage.style.aspectRatio =
            `${artworkAspectRatio} / 1`;
        } else if (
          artworkOrientation ===
          'landscape'
        ) {
          stage.classList.add(
            'is-landscape'
          );

          stage.style.aspectRatio =
            `${artworkAspectRatio} / 1`;
        } else {
          stage.classList.add(
            'is-portrait'
          );

          stage.style.aspectRatio =
            `${artworkAspectRatio} / 1`;
        }

        stage.append(
          artworkLayer,
          innerShadow,
          frame
        );

        composite.appendChild(stage);
        imageMain.appendChild(composite);

        previewStates.push({
          slide,
          root,
          composite,
          stage,
          frame,
          artworkLayer,
          innerShadow,
          artworkOrientation,
          artworkAspectRatio,

          naturalFrameUrl,
          blackFrameUrl,
          whiteFrameUrl,

          naturalLandscapeFrameUrl,
          blackLandscapeFrameUrl,
          whiteLandscapeFrameUrl,

          naturalSquareFrameUrl,
          blackSquareFrameUrl,
          whiteSquareFrameUrl
        });
      };

      artwork.addEventListener(
        'load',
        finishPreview,
        { once: true }
      );

      artwork.addEventListener(
        'error',
        () => {
          console.error(
            'Pink Fink artwork failed to load:',
            artworkUrl
          );
        },
        { once: true }
      );

      if (
        artwork.complete &&
        artwork.naturalWidth > 0
      ) {
        finishPreview();
      }
    });
  };

  const handlePinkFinkSelectorChange = (
    event
  ) => {
    const control = event.target;

    /*
     * Avoid .closest() because old custom theme code is
     * already throwing an event.target.closest error.
     */
    if (
      !(
        control instanceof
        HTMLSelectElement
      )
    ) {
      return;
    }

    const optionSelects =
      getProductOptionSelects(document);

    if (
      !optionSelects.includes(control)
    ) {
      return;
    }

    if (
      !isFrameSelect(control) &&
      !isSizeSelect(control)
    ) {
      return;
    }

    /*
     * Give Shopify time to update the selected values.
     */
    window.setTimeout(() => {
      previewStates.forEach((state) => {
        if (
          state.root !== document &&
          !state.root.contains(control)
        ) {
          return;
        }

        activatePreview(state);
      });
    }, 100);
  };

  document.addEventListener(
    'change',
    handlePinkFinkSelectorChange,
    true
  );

  document.addEventListener(
    'DOMContentLoaded',
    initialisePinkFinkPreview
  );

  document.addEventListener(
    'shopify:section:load',
    initialisePinkFinkPreview
  );

  initialisePinkFinkPreview();

  let pinkFinkResizeTimer;

  window.addEventListener(
    'resize',
    () => {
      window.clearTimeout(
        pinkFinkResizeTimer
      );

      pinkFinkResizeTimer =
        window.setTimeout(() => {
          document
            .querySelectorAll(
              '[data-pink-fink-frame-preview].pink-fink-ready'
            )
            .forEach((slide) => {
              syncPinkFinkThumbnailHeight(
                slide
              );
            });
        }, 150);
    }
  );
})();